import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import { Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { Text } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { DataHandle, Prescription } from '~/types'

import AdministrationSection, {
  type AdministrationSectionHandle,
  copyAdministrationInstructions,
} from './AdministrationSection'

const useStyles = makeStyles(
  (theme) => ({
    root: {
      position: 'relative',
    },
    expandCollapse: {
      display: 'flex',
      alignItems: 'center',
      position: 'absolute',
      top: theme.spacing(-3),
      right: 0,
      textDecoration: 'none',
      color: theme.colors.link,
    },
    administrationBlock: {
      border: theme.constants.tableBorderSelected,
      borderRadius: 2,
      padding: ({ isSimplified }: { isSimplified: boolean }) =>
        isSimplified ? 0 : theme.spacing(2),
      backgroundColor: theme.palette.common.white,
    },
    administrationBlockFlush: {
      paddingTop: ({ isSimplified }: { isSimplified: boolean }) =>
        isSimplified ? 0 : theme.spacing(1),
    },
  }),
  { name: 'PrescriptionDetailsSection' },
)

export interface PrescriptionDetailsSectionProps {
  alwaysExpanded?: boolean
  expandFormByDefault?: boolean
  isChewy?: boolean
  isExpandable?: boolean
  isLengthOfCourseRequired?: boolean
  isOrder?: boolean
  isRxPrescription?: boolean
  isVetDiet?: boolean
  onActiveAdminFrequencyChange?: (value: string) => void
  onRefChange?: () => void
  prescription: Prescription
}

export interface PrescriptionDetailsSectionHandle extends DataHandle {}

const PrescriptionDetailsSection = forwardRef<
  PrescriptionDetailsSectionHandle,
  PrescriptionDetailsSectionProps
>(function PrescriptionDetailsSection(
  {
    alwaysExpanded = false,
    expandFormByDefault = false,
    isChewy = false,
    isExpandable = false,
    isLengthOfCourseRequired = false,
    isOrder = false,
    isRxPrescription = false,
    isVetDiet = false,
    onActiveAdminFrequencyChange,
    onRefChange,
    prescription,
  },
  ref,
) {
  const { t } = useTranslation('Dialogs')

  const administrationSectionRef = useRef<AdministrationSectionHandle>(null)

  const [formExpanded, setFormExpanded] = useState(expandFormByDefault)

  const finalFormExpanded = alwaysExpanded || formExpanded

  const isCustomChewyNotesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CHEWY_PRESCRIPTION_NOTES),
  )

  const classes = useStyles({ isSimplified: !finalFormExpanded })

  useImperativeHandle(ref, () => ({
    get: () => ({
      ...(administrationSectionRef.current?.get() || {}),
    }),
    validate: () => {
      const isAdministrationSectionValid =
        administrationSectionRef.current?.validate() || false
      if (!isAdministrationSectionValid && !finalFormExpanded && isExpandable) {
        setFormExpanded(true)
      }
      return isAdministrationSectionValid
    },
  }))

  return (
    <Grid container className={classes.root}>
      {isExpandable && (
        <Text
          link
          strong
          className={classes.expandCollapse}
          variant="subheading3"
          onClick={() =>
            setFormExpanded((prevFormExpanded) => !prevFormExpanded)
          }
        >
          {finalFormExpanded
            ? t('Dialogs:PRESCRIPTION_DIALOG.COLLAPSE_FORM')
            : t('Dialogs:PRESCRIPTION_DIALOG.EXPAND_FORM')}
          {finalFormExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </Text>
      )}
      <Grid
        container
        className={classNames({
          [classes.administrationBlock]: isChewy,
          [classes.administrationBlockFlush]:
            isChewy && isCustomChewyNotesEnabled,
        })}
      >
        <AdministrationSection
          isChewy={isChewy}
          isLengthOfCourseRequired={isLengthOfCourseRequired}
          isOrder={isOrder}
          isRxPrescription={isRxPrescription}
          isVetDiet={isVetDiet}
          prescription={prescription}
          ref={administrationSectionRef}
          simplified={!finalFormExpanded}
          onActiveAdminFrequencyChange={onActiveAdminFrequencyChange}
          onRefChange={onRefChange}
        />
      </Grid>
    </Grid>
  )
})

export const copyPrescriptionDetailsSection = (prescription: Prescription) => ({
  ...copyAdministrationInstructions(prescription),
})

export default PrescriptionDetailsSection
